import { useState } from 'react'
import { Search, Globe, Sparkles, TrendingUp, Check } from 'lucide-react'
import DomainSearch from './components/DomainSearch'
import DomainResults from './components/DomainResults'
import Header from './components/Header'
import Footer from './components/Footer'
import { DomainResult } from './types'

function App() {
  const [results, setResults] = useState<DomainResult[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')

  const handleSearch = async (keyword: string, options: {
    tlds: string[]
    includeHyphen: boolean
    includePrefixSuffix: boolean
  }) => {
    setIsLoading(true)
    setSearchQuery(keyword)
    setResults([])

    try {
      // Generate suggestions
      const suggestResponse = await fetch('/api/suggest', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          keyword,
          ...options
        }),
      })

      if (!suggestResponse.ok) {
        throw new Error('Failed to generate suggestions')
      }

      const suggestions = await suggestResponse.json()
      
      // Set initial results with "checking" status
      const initialResults: DomainResult[] = suggestions.suggestions.map((domain: string) => ({
        domain,
        available: null,
        status: 'checking' as const,
        confidence: 'HIGH' as const,
        registrar: 'domain.com',
        affiliateLink: `/api/affiliate/${domain}?registrar=domain.com`
      }))

      setResults(initialResults)

      // Check availability for each domain
      const batchSize = 10
      for (let i = 0; i < suggestions.suggestions.length; i += batchSize) {
        const batch = suggestions.suggestions.slice(i, i + batchSize)
        
        const checkResponse = await fetch('/api/check-bulk', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            domains: batch
          }),
        })

        if (checkResponse.ok) {
          const batchResults = await checkResponse.json()
          
          setResults(prev => prev.map(result => {
            const updated = batchResults.results.find((r: any) => r.domain === result.domain)
            if (updated) {
              return {
                ...result,
                available: updated.available,
                status: updated.available ? 'available' as const : 'unavailable' as const,
                confidence: updated.confidence,
                method: updated.method,
                affiliateLink: updated.affiliateLink
              }
            }
            return result
          }))
        }

        // Small delay between batches
        if (i + batchSize < suggestions.suggestions.length) {
          await new Promise(resolve => setTimeout(resolve, 500))
        }
      }
    } catch (error) {
      console.error('Search error:', error)
      // Handle error - maybe show toast notification
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          {/* Logo and Title */}
          <div className="flex items-center justify-center mb-8">
            <div className="bg-gradient-to-br from-brand-100 to-accent-100 p-4 rounded-2xl mr-6 shadow-lg floating-card">
              <img src="/logo.svg" alt="Domain Brainstorm Logo" className="w-12 h-12" />
            </div>
            <div>
              <h1 className="text-5xl md:text-7xl font-black gradient-text">
                Domain Brainstorm
              </h1>
              <div className="text-sm font-medium text-slate-500 mt-2">
                Powered by AI • 100% Free • Unlimited Use
              </div>
            </div>
          </div>
          
          <p className="text-xl md:text-2xl text-slate-600 mb-12 max-w-3xl mx-auto leading-relaxed">
            Find the perfect domain name for your <span className="gradient-text font-semibold">startup</span>, 
            <span className="gradient-text font-semibold"> business</span>, or 
            <span className="gradient-text font-semibold"> project</span>. Generate creative suggestions 
            and check availability instantly across 50+ TLDs.
          </p>

          {/* Feature highlights with enhanced design */}
          <div className="grid md:grid-cols-3 gap-8 mb-16 max-w-5xl mx-auto">
            <div className="glass-card p-6 floating-card">
              <div className="bg-gradient-to-br from-brand-500 to-accent-500 p-3 rounded-xl w-fit mx-auto mb-4">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Creative AI Suggestions</h3>
              <p className="text-slate-600">Smart algorithms generate unique domain variations with prefixes, suffixes, and creative combinations.</p>
            </div>
            
            <div className="glass-card p-6 floating-card">
              <div className="bg-gradient-to-br from-success-500 to-success-600 p-3 rounded-xl w-fit mx-auto mb-4">
                <Check className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Instant Availability</h3>
              <p className="text-slate-600">Real-time domain checking across major registrars using DNS and official registry APIs.</p>
            </div>
            
            <div className="glass-card p-6 floating-card">
              <div className="bg-gradient-to-br from-accent-500 to-brand-500 p-3 rounded-xl w-fit mx-auto mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-bold text-lg mb-2">Zero Cost, Unlimited</h3>
              <p className="text-slate-600">Completely free with no limits, no ads, and no hidden fees. Check as many domains as you want.</p>
            </div>
          </div>
        </div>

        {/* Search Section */}
        <DomainSearch onSearch={handleSearch} isLoading={isLoading} />

        {/* Results Section */}
        {results.length > 0 && (
          <DomainResults 
            results={results} 
            searchQuery={searchQuery}
            isLoading={isLoading}
          />
        )}

        {/* How it works section */}
        {results.length === 0 && (
          <div className="mt-16">
            <h2 className="text-3xl font-bold text-center mb-8">How It Works</h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="bg-primary-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Search className="w-8 h-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">1. Enter Your Idea</h3>
                <p className="text-gray-600">Type in your business name, keywords, or any idea you have in mind.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Sparkles className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">2. Get Suggestions</h3>
                <p className="text-gray-600">Our AI generates creative domain variations with different TLDs and combinations.</p>
              </div>
              
              <div className="text-center">
                <div className="bg-success-100 p-4 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <Globe className="w-8 h-8 text-success-600" />
                </div>
                <h3 className="text-xl font-semibold mb-2">3. Check & Buy</h3>
                <p className="text-gray-600">See availability in real-time and purchase your perfect domain with our partner registrars.</p>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}

export default App
